import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import "./UploadProduct.css";
import API_BASE_URL from "../../api";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ===== Category code map =====
const CATEGORY_CODES = {
  "Disc Assembly & Disc Bearings": "DSC",
  "Disc Assemby & Disc Bearings": "DSC",
  "Frog": "FRG",
  "Skimmer & Skimmer Arm": "SKM",
  "Headstock": "HDK",
  "Landslide": "LND",
  "Mouldboards": "MLD",
  "Points & Wings": "PTW",
  "Wheel": "WHL",
  "Other Parts": "OTH",
  "Plough Parts": "PLG",
};

const getCategoryCode = (name) => {
  if (!name) return "???";
  return CATEGORY_CODES[name] || name.substring(0, 3).toUpperCase();
};

// ===== Sortable image card =====
const SortablePhoto = ({ img, index, total, onRemove, onMoveLeft, onMoveRight }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
      useSortable({ id: img.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 999 : "auto",
  };

  return (
      <div ref={setNodeRef} style={style} className={`photo-card ${isDragging ? "photo-card-dragging" : ""}`}>
        <div className="photo-drag-handle" {...attributes} {...listeners}>⠿</div>
        <img src={img.type === "saved" ? img.url : img.preview} alt={img.fileName} className="photo-img" />
        {index === 0 && <div className="photo-cover-badge">Cover</div>}
        <button type="button" className="photo-remove" onClick={() => onRemove(img.id)} aria-label="Remove photo">✕</button>
        <div className="photo-arrow-btns">
          <button type="button" className="photo-arrow" onClick={() => onMoveLeft(index)} disabled={index === 0} aria-label="Move left">‹</button>
          <button type="button" className="photo-arrow" onClick={() => onMoveRight(index)} disabled={index === total - 1} aria-label="Move right">›</button>
        </div>
      </div>
  );
};

// ===== Main component =====
const ProductUpload = ({ editMode }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [model, setModel] = useState("");
  const [condition, setCondition] = useState("");
  const [year, setYear] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [mpn, setMpn] = useState("");
  const [priceOnApplication, setPriceOnApplication] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [parentCategories, setParentCategories] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState("");
  const [subcategories, setSubcategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [allCategoriesFlat, setAllCategoriesFlat] = useState([]);

  const [previewCode, setPreviewCode] = useState("");
  const [images, setImages] = useState([]);

  // ===== Copy-from-existing-listing live search =====
  const [allProductsForCopy, setAllProductsForCopy] = useState([]);
  const [copyQuery, setCopyQuery] = useState("");
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyHighlight, setCopyHighlight] = useState(-1);
  const copyBoxRef = useRef(null);
  const copyBlurTimer = useRef(null);

  const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
      useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  useEffect(() => {
    fetchCategories();
    fetchAllProductsForCopy();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!selectedParentId) { setSubcategories([]); setCategoryId(""); setPreviewCode(""); return; }
    const subs = allCategoriesFlat.filter((c) => String(c.parent_id) === String(selectedParentId));
    setSubcategories(subs);
    if (categoryId && !subs.find((s) => String(s.id) === String(categoryId))) setCategoryId("");
    // eslint-disable-next-line
  }, [selectedParentId, allCategoriesFlat]);

  useEffect(() => {
    const activeCatId = categoryId || selectedParentId;
    if (!activeCatId) { setPreviewCode(""); return; }

    const cat = allCategoriesFlat.find((c) => String(c.id) === String(activeCatId));
    if (!cat) return;

    const code = getCategoryCode(cat.name);

    axios.get(`${API_BASE_URL}/api/products/next-stock-number`, { params: { categoryId: activeCatId } })
        .then((res) => {
          const preview = res.data?.preview || "PB-???-???-???";
          setPreviewCode(preview);
        })
        .catch(() => {
          setPreviewCode(`PB-${code}-???-???`);
        });
    // eslint-disable-next-line
  }, [categoryId, selectedParentId, allCategoriesFlat]);

  useEffect(() => {
    if (editMode && id && allCategoriesFlat.length > 0) fetchProduct(id);
    // eslint-disable-next-line
  }, [editMode, id, allCategoriesFlat]);

  useEffect(() => {
    return () => { images.forEach((img) => img.preview && URL.revokeObjectURL(img.preview)); };
  }, [images]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/categories`);
      const flat = res.data || [];
      setAllCategoriesFlat(flat);
      setParentCategories(flat.filter((c) => !c.parent_id));
    } catch { setError("Failed to load categories"); }
  };

  const fetchAllProductsForCopy = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/products/all`);
      setAllProductsForCopy((res.data || []).filter((p) => p.name !== "__draft__" && !p.name?.startsWith?.("__draft__")));
    } catch {
      // Non-critical — copy search just won't have suggestions
    }
  };

  const fetchProduct = async (productId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/products/${productId}`);
      const product = res.data;
      setName(product.name || "");
      setDescription(product.description || "");
      setPrice(product.price || "");
      setManufacturer(product.manufacturer || "");
      setModel(product.model || "");
      setCondition(product.condition || "");
      setYear(product.year || "");
      setQuantity(product.quantity || 1);
      setMpn(product.mpn || "");
      setPriceOnApplication(product.price_on_application || false);

      const catId = product.category_id;
      if (catId) {
        const cat = allCategoriesFlat.find((c) => c.id === catId);
        if (cat) {
          if (cat.parent_id) { setSelectedParentId(String(cat.parent_id)); setCategoryId(String(catId)); }
          else { setSelectedParentId(String(catId)); setCategoryId(String(catId)); }
        }
      }

      const savedFiles = product.files || [];
      const displayUrl = product.display_image;
      let ordered = [...savedFiles];
      if (displayUrl) {
        const idx = ordered.findIndex((f) => f.url === displayUrl);
        if (idx > 0) { const [c] = ordered.splice(idx, 1); ordered.unshift(c); }
      }
      setImages(ordered.map((f, i) => ({ id: `saved-${i}-${f.url}`, type: "saved", url: f.url, fileName: f.fileName })));
    } catch { setError("Failed to load product data"); }
  };

  // ===== Copy-from-existing-listing: live filter as you type =====
  const copyResults = useMemo(() => {
    const q = copyQuery.trim().toLowerCase();
    if (!q) return [];
    return allProductsForCopy
        .filter((p) => !editMode || String(p.id) !== String(id))
        .filter((p) =>
            (p.name || "").toLowerCase().includes(q) ||
            (p.manufacturer || "").toLowerCase().includes(q) ||
            (p.model || "").toLowerCase().includes(q) ||
            (p.stock_number || "").toLowerCase().includes(q)
        )
        .slice(0, 8);
  }, [copyQuery, allProductsForCopy, editMode, id]);

  const applyCopy = (product) => {
    setName(product.name || "");
    setDescription(product.description || "");
    setPrice(product.price || "");
    setPriceOnApplication(product.price_on_application || false);
    setManufacturer(product.manufacturer || "");
    setModel(product.model || "");
    setCondition(product.condition || "");
    setYear(product.year || "");
    setQuantity(product.quantity || 1);
    setMpn(product.mpn || "");

    const catId = product.category_id;
    if (catId) {
      const cat = allCategoriesFlat.find((c) => c.id === catId);
      if (cat) {
        if (cat.parent_id) { setSelectedParentId(String(cat.parent_id)); setCategoryId(String(catId)); }
        else { setSelectedParentId(String(catId)); setCategoryId(""); }
      }
    } else {
      setSelectedParentId("");
      setCategoryId("");
    }

    setCopyQuery("");
    setCopyOpen(false);
    setCopyHighlight(-1);
    setSuccess(`Copied details from "${product.name}"${product.stock_number ? ` (${product.stock_number})` : ""}. Photos were not copied — add fresh ones below.`);
    setError("");
  };

  const handleCopyKeyDown = (e) => {
    if (!copyOpen || copyResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCopyHighlight((h) => Math.min(h + 1, copyResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCopyHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (copyHighlight >= 0) {
        e.preventDefault();
        applyCopy(copyResults[copyHighlight]);
      }
    } else if (e.key === "Escape") {
      setCopyOpen(false);
    }
  };

  const handleCopyBlur = () => {
    // Delay so a click on a dropdown item registers before we close it
    copyBlurTimer.current = setTimeout(() => setCopyOpen(false), 150);
  };

  const handleCopyFocus = () => {
    if (copyBlurTimer.current) clearTimeout(copyBlurTimer.current);
    if (copyQuery.trim()) setCopyOpen(true);
  };

  const onDrop = useCallback((acceptedFiles) => {
    const mapped = acceptedFiles.map((file) => ({
      id: `new-${Date.now()}-${Math.random()}`,
      type: "new", file,
      preview: URL.createObjectURL(file),
      fileName: file.name,
    }));
    setImages((prev) => [...prev, ...mapped]);
  }, []);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop, multiple: true, accept: { "image/*": [] }, noClick: true, noKeyboard: true,
  });

  const handleRemove = async (imgId) => {
    const img = images.find((i) => i.id === imgId);
    if (!img) return;
    if (img.type === "saved") {
      try {
        await axios.delete(`${API_BASE_URL}/api/products/delete-file`, { data: { url: img.url } });
      } catch (err) {
        setError("Failed to delete file: " + (err.response?.data?.error || err.message));
        return;
      }
    }
    setImages((prev) => prev.filter((i) => i.id !== imgId));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setImages((prev) => {
      const oldIndex = prev.findIndex((i) => i.id === active.id);
      const newIndex = prev.findIndex((i) => i.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleMoveLeft = (index) => {
    if (index === 0) return;
    setImages((prev) => arrayMove(prev, index, index - 1));
  };

  const handleMoveRight = (index) => {
    setImages((prev) => {
      if (index === prev.length - 1) return prev;
      return arrayMove(prev, index, index + 1);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");

    const finalCategoryId = categoryId || selectedParentId;
    if (!finalCategoryId) { setError("Please select a category."); return; }

    if (subcategories.length > 0 && !categoryId) {
      setError("Please select a sub-category.");
      return;
    }

    try {
      const newImages = images.filter((img) => img.type === "new");
      let uploadedNew = [];
      let productId = editMode ? id : null;

      if (newImages.length > 0) {
        // Only init a draft row if we actually need to upload images (to get an ID for the S3 path)
        if (!editMode) {
          productId = (await axios.post(`${API_BASE_URL}/api/products/init`)).data.id;
        }
        const formData = new FormData();
        formData.append("productId", productId);
        newImages.forEach((img) => formData.append("files", img.file));
        const uploadRes = await axios.post(`${API_BASE_URL}/api/products/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploadedNew = uploadRes.data || [];
      }

      let newIdx = 0;
      const finalFiles = images.map((img) => {
        if (img.type === "saved") return { fileName: img.fileName, url: img.url };
        const uploaded = uploadedNew[newIdx++];
        return { fileName: uploaded.fileName, url: uploaded.url };
      });

      const productData = {
        name, description,
        price: priceOnApplication ? null : price,
        price_on_application: priceOnApplication,
        categoryId: finalCategoryId,
        manufacturer, model, condition, year, quantity, mpn,
        files: finalFiles,
        display_image: finalFiles.length > 0 ? finalFiles[0].url : null,
        enabled: true,
      };

      if (editMode || productId) {
        // Edit mode, or add mode with images (draft was created) — use PUT
        await axios.put(`${API_BASE_URL}/api/products/${productId}`, productData);
      } else {
        // Add mode, no images — go straight to POST (no draft needed)
        await axios.post(`${API_BASE_URL}/api/products`, productData);
      }
      navigate("/admin/products");

    } catch (err) {
      if (err.response?.status === 409) {
        setError("⚠️ Stock number conflict — a duplicate was detected. Please try saving again to generate a new stock number, or contact support if this keeps happening.");
      } else {
        setError(err.response?.data?.error || err.message || "Something went wrong. Please try again.");
      }
    }
  };

  const hasImages = images.length > 0;
  const activeSubName = categoryId
      ? allCategoriesFlat.find((c) => String(c.id) === String(categoryId))?.name
      : null;
  const activeParentName = selectedParentId
      ? parentCategories.find((p) => String(p.id) === String(selectedParentId))?.name
      : null;

  return (
      <div className="upload-container">
        <h1>{editMode ? "Edit Product" : "Upload Product"}</h1>

        {error && <p className="upload-msg upload-msg-error">{error}</p>}
        {success && <p className="upload-msg upload-msg-success">{success}</p>}

        <form onSubmit={handleSubmit}>

          {/* ===== COPY FROM EXISTING LISTING ===== */}
          <div className="upload-field copy-search-field" ref={copyBoxRef}>
            <label>Copy details from an existing listing</label>
            <p className="upload-hint">
              Got another one of these? Start typing its name, stock number, manufacturer or model, then pick it to fill in the fields below. Photos are never copied.
            </p>
            <input
                type="text"
                autoComplete="off"
                placeholder="Start typing to search existing products…"
                value={copyQuery}
                onChange={(e) => { setCopyQuery(e.target.value); setCopyOpen(true); setCopyHighlight(-1); }}
                onFocus={handleCopyFocus}
                onBlur={handleCopyBlur}
                onKeyDown={handleCopyKeyDown}
            />
            {copyOpen && copyQuery.trim() && (
                <ul className="copy-search-results">
                  {copyResults.length === 0 ? (
                      <li className="copy-search-empty">No matching products found</li>
                  ) : (
                      copyResults.map((p, i) => (
                          <li
                              key={p.id}
                              className={`copy-search-item ${i === copyHighlight ? "copy-search-item-active" : ""}`}
                              onMouseDown={(e) => { e.preventDefault(); applyCopy(p); }}
                              onMouseEnter={() => setCopyHighlight(i)}
                          >
                            <span className="copy-search-name">{p.name}</span>
                            <span className="copy-search-meta">
                              {[p.stock_number, p.manufacturer, p.model].filter(Boolean).join(" · ") || "—"}
                            </span>
                          </li>
                      ))
                  )}
                </ul>
            )}
          </div>

          <div className="upload-field">
            <label>Product Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>

          <div className="upload-field">
            <label>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
          </div>

          {/* ===== TWO-STEP CATEGORY ===== */}
          <div className="upload-field">
            <label>Category *</label>
            <select value={selectedParentId} onChange={(e) => { setSelectedParentId(e.target.value); setCategoryId(""); }} required>
              <option value="">Select a category</option>
              {parentCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Sub-category — required if subcategories exist */}
          {subcategories.length > 0 && (
              <div className="upload-field">
                <label>Sub-category *</label>
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required>
                  <option value="">Select a sub-category</option>
                  {subcategories.map((sub) => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                  ))}
                </select>
              </div>
          )}

          {/* ===== PREVIEW STOCK CODE ===== */}
          {previewCode && !editMode && (
              <div className="stock-code-preview">
                <span className="stock-code-label">Stock code will be</span>
                <span className="stock-code-value">{previewCode}</span>
                <span className="stock-code-hint">
                  {activeSubName || activeParentName} — assigned on save
                </span>
              </div>
          )}

          <div className="upload-field">
            <label>Manufacturer</label>
            <input type="text" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} />
          </div>

          <div className="upload-field">
            <label>Model</label>
            <input type="text" value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. LB85, LD85" />
          </div>

          <div className="upload-field">
            <label>Condition</label>
            <select value={condition} onChange={(e) => setCondition(e.target.value)}>
              <option value="">Select condition</option>
              <option value="New">New</option>
              <option value="Used">Used</option>
            </select>
          </div>

          <div className="upload-field">
            <label>Year</label>
            <input type="text" value={year} onChange={(e) => setYear(e.target.value)} />
          </div>

          <div className="upload-field">
            <label>MPN (Manufacturer Part Number)</label>
            <input type="text" value={mpn} onChange={(e) => setMpn(e.target.value)} placeholder="e.g. 12345-ABC" />
          </div>

          <div className="upload-field">
            <label>Quantity</label>
            <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </div>

          <div className="upload-field">
            <label>Price</label>
            <div className="price-row">
              <input
                  type="number" value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  disabled={priceOnApplication}
                  className={priceOnApplication ? "price-disabled" : ""}
              />
              <label className="poa-label">
                <input type="checkbox" checked={priceOnApplication} onChange={(e) => setPriceOnApplication(e.target.checked)} />
                Price on application
              </label>
            </div>
          </div>

          {/* ===== PHOTOS ===== */}
          <div className="upload-field">
            <label>Photos</label>
            <p className="upload-hint">Drag to reorder or use the arrows — <strong>the first image is the cover photo</strong></p>

            <div {...getRootProps()} className={`photo-dropzone ${isDragActive ? "is-active" : ""}`}>
              <input {...getInputProps()} />

              {!hasImages ? (
                  <div className="photo-dropzone-empty">
                    <div className="photo-dropzone-icon">🖼️</div>
                    <div className="photo-dropzone-title">Drag and drop photos here</div>
                    <button type="button" className="photo-dropzone-btn" onClick={(e) => { e.stopPropagation(); open(); }}>
                      Upload from computer
                    </button>
                    {isDragActive && <div className="photo-dropzone-hint">Drop files here…</div>}
                  </div>
              ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={images.map((i) => i.id)} strategy={rectSortingStrategy}>
                      <div className="photo-grid">
                        {images.map((img, index) => (
                            <SortablePhoto key={img.id} img={img} index={index} total={images.length} onRemove={handleRemove} onMoveLeft={handleMoveLeft} onMoveRight={handleMoveRight} />
                        ))}
                        <button type="button" className="photo-card photo-add" onClick={(e) => { e.stopPropagation(); open(); }}>
                          <div className="photo-add-plus">＋</div>
                          <div className="photo-add-text">Add</div>
                        </button>
                      </div>
                    </SortableContext>
                  </DndContext>
              )}
            </div>
          </div>

          <button type="submit" className="upload-submit">
            {editMode ? "Update Product" : "Upload Product"}
          </button>

        </form>
      </div>
  );
};

export default ProductUpload;